import Skeleton from "react-loading-skeleton";
import { generateAbbreviation } from "../../../../../lib/util";
import { useFormatCurrency } from "../../../../common/format-currency";
import {
  calculateCartTotal,
  createCartCheckoutComponents,
} from "../../shared/cart/base-cart-checkout";
import UserLoginSwitch from "../user-login-switch";
import PaymentMethod from "./payment-method";
import styles from "../index.module.scss";

const renderConvetiCartItemMedia = ({ item, imageLoaded, setImageLoaded }) => {
  if (!item.mediaThumb) {
    return (
      <div
        className={`${styles.abbrevatedDiv} ${styles.cartItemMedia}`}
        title={item.name}
      >
        {generateAbbreviation(item.name)}
      </div>
    );
  }

  return (
    <div className={styles.cartItemMedia}>
      {!imageLoaded && <Skeleton width="100%" height="100%" />}
      {imageLoaded && (
        <img
          src={item.mediaThumb.replace("r200x200", "r300x300")}
          alt={item.name}
        />
      )}
      <img
        src={item.mediaThumb.replace("r200x200", "r300x300")}
        alt=""
        style={{ display: "none" }}
        onLoad={() => setImageLoaded(true)}
      />
    </div>
  );
};

const { CartCheckout, CartOrderDetails, CartSummary } =
  createCartCheckoutComponents({
    styles,
    UserLoginSwitch,
    PaymentMethod,
    renderCartItemMedia: renderConvetiCartItemMedia,
    enableOrderDetailsSync: true,
  });

export const CartCheckoutSummary = ({
  cart,
  accessPoint,
  paymentChargeData,
  paymentMethod,
}) => {
  const { formatCurrency } = useFormatCurrency();
  const disableStoreProductPrice =
    accessPoint?.settings?.disableStoreFrontPrice || false;
  const cartItems = {
    ...cart,
    paymentItem: cart?.paymentItem ?? [],
  };
  const currency =
    cartItems.currency || cartItems.paymentItem[0]?.currency || cart?.currency;
  const itemCount = cartItems.paymentItem.reduce(
    (total, item) => total + Number(item?.unit || 0),
    0,
  );
  const subtotal = cartItems.amount ?? calculateCartTotal(cartItems);
  const deliveryAmount = Number(cartItems.deliveryAmount || 0);
  const gatewayFee =
    paymentChargeData &&
    paymentChargeData.transactionChargeBearer === "USER" &&
    paymentMethod === "ONLINE"
      ? (paymentChargeData.processingFee || 0) +
        (paymentChargeData.addTransactionCharge
          ? paymentChargeData.toCharge || 0
          : 0)
      : 0;
  const total =
    cartItems.totalAmount ??
    subtotal +
      deliveryAmount +
      Number(cartItems.vatAmount || 0) -
      Number(cartItems.discountAmount || 0) +
      gatewayFee;

  if (!cartItems.paymentItem.length) return null;

  return (
    <div className={styles.convetiOrderSummary}>
      <div className={styles.convetiOrderSummaryHeader}>
        <h2>Order Summary</h2>
        <span>
          {itemCount} {itemCount === 1 ? "Item" : "Items"}
        </span>
      </div>

      {disableStoreProductPrice ? (
        <div className={styles.convetiSummaryNotice}>
          Seller will confirm pricing after you place the order.
        </div>
      ) : (
        <>
          <div className={styles.convetiSummaryDivider} />

          <div className={styles.convetiSummaryRow}>
            <span>Subtotal:</span>
            <strong>{formatCurrency(subtotal, currency)}</strong>
          </div>

          <div className={styles.convetiSummaryRow}>
            <span>Delivery Charges:</span>
            {deliveryAmount > 0 ? (
              <strong>{formatCurrency(deliveryAmount, currency)}</strong>
            ) : (
              <p>
                Delivery charges not included yet. Add your delivery address at
                checkout to see your delivery charges
              </p>
            )}
          </div>

          {gatewayFee > 0 && (
            <div className={styles.convetiSummaryRow}>
              <span>Processing Fee:</span>
              <strong>
                {formatCurrency(
                  gatewayFee,
                  paymentChargeData.currency || currency,
                )}
              </strong>
            </div>
          )}

          <div className={styles.convetiSummaryDivider} />

          <div
            className={`${styles.convetiSummaryRow} ${styles.convetiSummaryTotal}`}
          >
            <span>Total</span>
            <strong>{formatCurrency(total, currency)}</strong>
          </div>
        </>
      )}

      <div className={styles.convetiSummaryDivider} />

      <div className={styles.convetiAcceptedPayments}>
        <span>We accept:</span>
        <div>
          <strong className={styles.mastercardBadge}>Mastercard</strong>
          <strong className={styles.visaBadge}>VISA</strong>
          <strong className={styles.verveBadge}>Verve</strong>
        </div>
      </div>

      <div className={styles.convetiSecureNotice}>
        <span aria-hidden="true" />
        <p>Transactions are 100% Safe and Secure</p>
      </div>
    </div>
  );
};

export { CartOrderDetails, CartSummary };
export default CartCheckout;
